import {create} from 'zustand';
import { axiosInstance } from '../services/axios-instance';
import { LabResultsService } from '../services/lab-results-service/service';
import type { CreateLabResultInput, LabResult, LabResultList } from '../services/lab-results-service/types';
import { parseAxiosError } from '../utils';

interface LabResultsState {
  results: LabResult[]
  currentLabResult: LabResult | null
  currentPage: Number,
  totalPages: Number,
  serverError: string,
  createLabResult: {(input: CreateLabResultInput): Promise<LabResult>}
  uploadLabResultByCSV: {(file: File): Promise<void>}
  getLabResultById: {(id: number): Promise<LabResult>}
  listLabResults: {(page: number): Promise<LabResultList>}
  setCurrentLabResult: {(labResult: LabResult): void}
  clearCurrentLabResult: {(): void}
}

const initialState = {
  results: [] as LabResult[], // for paginated results
  loadingResults: false,
  currentPage: 1,
  totalPages: 1,
  currentLabResult: null, // just so we can reuse the response sent by create and update endpoints
  serverError: '',
};

const labResultsService = new LabResultsService(axiosInstance);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useLabResultsStore = create<LabResultsState>((set, get) => ({
  ...initialState,
  createLabResult: async (input): Promise<LabResult> => {
    const data = await labResultsService.create(input);
    set(() => ({ currentLabResult: data }));
    return data;
  },
  uploadLabResultByCSV: async (file: File): Promise<void> => {
    try {
      await labResultsService.uploadCsv(file);
    } catch (error) {
      set(() => ({ serverError: parseAxiosError(error) }));
      throw error;
    }
    set(() => ({ serverError: '' }));
  },
  getLabResultById: async (id: number): Promise<LabResult> => {
    // To minimize API calls
    const current = get().currentLabResult;
    if (current?.id === id) {
      return current;
    }
    const labResult = await labResultsService.getById(id);
    set(() => ({ currentLabResult: labResult }));
    return labResult;
  },
  listLabResults: async (page: number): Promise<LabResultList> => {
    const data = await labResultsService.list(page);
    set(() => ({
      results: data.items,
      currentPage: Number(data.page),
      totalPages: Number(data.totalPages),
    }));
    return data;
  },
  setCurrentLabResult: (labResult: LabResult) => {
    set(() => ({ currentLabResult: labResult }));
  },
  clearCurrentLabResult: () => {
    set(() => ({ currentLabResult: null }));
  },
}));
